import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle, Bell, Compass, Cpu, FileText, Fuel, Gauge, History,
  Info, MapPin, Power, Radio, Send, SignalHigh, Terminal, Truck, User, X, Zap,
} from 'lucide-react';
import { cn } from '../../../../utils/cn';
import { Badge } from '../../../../components/ui/Badge';
import {
  alarmaLabel, comandoEstadoMeta, comandoLabel, estadoMeta, rumboCardinal,
  severidadLabel, severidadTone, voltajeTone,
  formatDateTime, formatNumber, formatRelative,
  gpsApi,
} from '../gpsMock';
import { SignalBars } from './SignalBars';
import { BatteryGauge } from './BatteryGauge';

// estado → token de estado (el hex de gpsMock es solo para la capa Leaflet).
const ESTADO_DOT = {
  movimiento: 'bg-status-success',
  detenido:   'bg-status-info',
  inactivo:   'bg-status-warning',
  sin_senal:  'bg-status-neutral',
};

/**
 * Panel de detalle del dispositivo seleccionado en el monitor. Tabs internos
 * (Resumen / Comandos / Alarmas / Ficha) sin depender de un componente Tabs
 * compartido — controla un estado local. La feature es MOCKUP; cualquier
 * acción de comando solo registra UI feedback.
 */
export function DeviceDetailPanel({ device, live, alarmas, onClose, className }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('resumen');
  const meta = estadoMeta[device.estado];
  const vel = live?.velocidadKmh ?? device.velocidadKmh;
  const rumbo = live?.rumbo ?? device.rumbo;
  const pos = live?.pos ?? device.pos;
  const alarmasDevice = alarmas.filter((a) => a.deviceId === device.id);
  const alarmasActivas = alarmasDevice.filter((a) => !a.resuelta);

  const tabs = [
    { id: 'resumen',  icon: <Info     className="w-3.5 h-3.5" />, label: t('Resumen') },
    { id: 'comandos', icon: <Terminal className="w-3.5 h-3.5" />, label: t('Comandos') },
    { id: 'alarmas',  icon: <Bell     className="w-3.5 h-3.5" />, label: t('Alarmas'), badge: alarmasActivas.length },
    { id: 'ficha',    icon: <FileText className="w-3.5 h-3.5" />, label: t('Ficha') },
  ];

  return (
    <aside className={cn('flex flex-col h-full bg-bg border-l border-border shadow-elevated', className)}>
      {/* Header */}
      <header className="px-4 pt-4 pb-3 border-b border-border">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span
                className={cn('w-2.5 h-2.5 rounded-full shrink-0', ESTADO_DOT[device.estado])}
                aria-hidden
              />
              <Badge tone={meta.tone}>{t(meta.label)}</Badge>
              {alarmasActivas.length > 0 && (
                <Badge tone="error" dot>
                  {alarmasActivas.length} {t("alarma")}{alarmasActivas.length === 1 ? '' : 's'}
                </Badge>
              )}
            </div>
            <h2 className="mt-2 font-mono text-xl font-semibold tracking-tight text-fg">
              {device.patente}
            </h2>
            <div className="text-sm text-fg-muted mt-0.5 truncate">
              {device.alias} · {device.vehiculoMarca} {device.vehiculoModelo}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-fg-muted hover:text-fg p-1.5 rounded-md hover:bg-bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            aria-label={t("Cerrar")}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </header>

      <div className="flex-1 min-h-0 flex flex-col">
        <div className="px-4 pt-2 border-b border-border">
          <div className="flex items-center gap-1 -mb-px overflow-x-auto">
            {tabs.map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 h-9 text-xs font-medium border-b-2 transition-colors whitespace-nowrap',
                    active
                      ? 'border-brand-500 text-brand-700'
                      : 'border-transparent text-fg-muted hover:text-fg',
                  )}
                >
                  {t.icon} {t.label}
                  {t.badge > 0 && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-status-error text-white">
                      {t.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {tab === 'resumen'  && <ResumenTab  device={device} vel={vel} rumbo={rumbo} pos={pos} />}
          {tab === 'comandos' && <ComandosTab device={device} />}
          {tab === 'alarmas'  && <AlarmasTab  alarmas={alarmasDevice} />}
          {tab === 'ficha'    && <FichaTab    device={device} />}
        </div>
      </div>
    </aside>
  );
}

// ── Tabs ────────────────────────────────────────────────────────────────────

function ResumenTab({ device, vel, rumbo, pos }) {
  const { t } = useTranslation();
  const r = device.resumenDia;
  return (
    <div className="space-y-5">
      <section>
        <SectionLabel icon={<Gauge className="w-3.5 h-3.5" />}>{t("Telemetría en vivo")}</SectionLabel>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <KpiTile
            label={t("Velocidad")}
            value={device.estado === 'movimiento' ? `${vel}` : '0'}
            unit="km/h"
            hint={device.estado === 'movimiento' && vel > device.velocidadMaxKmh ? `${t("máx permitida")} ${device.velocidadMaxKmh}` : undefined}
            danger={device.estado === 'movimiento' && vel > device.velocidadMaxKmh}
          />
          <KpiTile
            label={t("Rumbo")}
            value={device.estado === 'movimiento' ? `${rumbo}°` : '—'}
            unit={device.estado === 'movimiento' ? rumboCardinal(rumbo) : undefined}
          />
          <KpiTile label={t("Encendido")} value={device.encendido ? t('Sí') : t('No')} icon={<Power className="w-3.5 h-3.5" />} />
          <KpiTile label={t("Satélites")} value={`${device.satelites}`} icon={<Compass className="w-3.5 h-3.5" />} />
        </div>
      </section>

      <section>
        <SectionLabel icon={<MapPin className="w-3.5 h-3.5" />}>{t("Última posición")}</SectionLabel>
        <p className="text-sm text-fg mt-2">{device.direccion}</p>
        <div className="mt-1.5 flex items-center gap-3 text-xs text-fg-muted">
          <span className="font-mono tabular-nums">{pos.lat.toFixed(5)}, {pos.lng.toFixed(5)}</span>
          <span>·</span>
          <span>{formatRelative(device.ultimoReporte)}</span>
        </div>
        {device.detenidoDesde && device.estado !== 'movimiento' && (
          <div className="mt-1 text-xs text-fg-muted">
            {t("Detenido desde")} {formatDateTime(device.detenidoDesde)}
          </div>
        )}
      </section>

      <section>
        <SectionLabel icon={<Radio className="w-3.5 h-3.5" />}>{t("Diagnóstico del equipo")}</SectionLabel>
        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
          <Row label={t("Señal GSM")}>
            <div className="flex items-center gap-2">
              <SignalBars level={device.senalGsm} />
              <span className="tabular-nums text-fg-muted text-xs">{device.senalGsm}/4</span>
            </div>
          </Row>
          <Row label={t("Batería")}><BatteryGauge pct={device.bateriaPct} conectada={device.bateriaConectada} /></Row>
          <Row label={t("Voltaje veh.")}>
            <Badge tone={voltajeTone(device.bateriaExternaV)} dot>
              {device.bateriaExternaV ? `${device.bateriaExternaV.toFixed(1)} V` : '—'}
            </Badge>
          </Row>
          <Row label={t("Odómetro")}>
            <span className="tabular-nums text-fg">{formatNumber(device.odometroKm)} km</span>
          </Row>
        </div>
      </section>

      <section>
        <SectionLabel icon={<History className="w-3.5 h-3.5" />}>{t("Resumen del día")}</SectionLabel>
        <div className="mt-2 grid grid-cols-3 gap-2">
          <MiniStat label={t("Km")} value={r.distanciaKm.toFixed(0)} />
          <MiniStat label={t("Encendidos")} value={`${r.encendidos}`} />
          <MiniStat label={t("Hrs motor")} value={`${Math.floor(r.tiempoEncendidoMin / 60)}:${String(r.tiempoEncendidoMin % 60).padStart(2, '0')}`} />
          <MiniStat label={t("Exc. veloc.")} value={`${r.excesosVelocidad}`} tone={r.excesosVelocidad > 0 ? 'warning' : undefined} />
          <MiniStat label={t("Frenadas")} value={`${r.frenadasBruscas}`} tone={r.frenadasBruscas > 3 ? 'warning' : undefined} />
          <MiniStat label={t("Aceler.")} value={`${r.aceleracionesBruscas}`} tone={r.aceleracionesBruscas > 3 ? 'warning' : undefined} />
        </div>
      </section>
    </div>
  );
}

function ComandosTab({ device }) {
  const { t } = useTranslation();
  const [history, setHistory] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // TODO(back): cablear cuando el back exponga /devices/:id/commands. Por ahora mock.
    gpsApi.comandos(device.id).then((c) => { if (!cancelled) setHistory(c); });
    return () => { cancelled = true; };
  }, [device.id]);

  const acciones = [
    { tipo: 'ubicacion_solicitar', icon: <MapPin className="w-4 h-4" /> },
    { tipo: 'reporte_intervalo',   icon: <Radio  className="w-4 h-4" /> },
    { tipo: 'reiniciar',           icon: <Zap    className="w-4 h-4" /> },
    { tipo: 'motor_bloquear',      icon: <Power  className="w-4 h-4" />, variant: 'danger' },
    { tipo: 'motor_desbloquear',   icon: <Power  className="w-4 h-4" /> },
    { tipo: 'corte_combustible',   icon: <Fuel   className="w-4 h-4" />, variant: 'danger' },
  ];

  return (
    <div className="space-y-5">
      <section>
        <SectionLabel icon={<Send className="w-3.5 h-3.5" />}>{t("Enviar comando")}</SectionLabel>
        <p className="text-xs text-fg-muted mt-1.5">
          {t("Mockup. En producción se firma con el JWT del operador y se entrega vía adapter Traccar.")}
        </p>
        <div className="grid grid-cols-2 gap-2 mt-3">
          {acciones.map((a) => (
            <button
              key={a.tipo}
              type="button"
              className={cn(
                'inline-flex items-center gap-2 px-3 h-9 text-xs font-medium rounded-lg border transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                a.variant === 'danger'
                  ? 'border-status-error/40 text-status-error hover:bg-status-error-soft'
                  : 'border-border text-fg hover:bg-bg-muted',
              )}
            >
              {a.icon}
              <span className="truncate">{t(comandoLabel[a.tipo])}</span>
            </button>
          ))}
        </div>
      </section>

      <section>
        <SectionLabel icon={<History className="w-3.5 h-3.5" />}>{t("Historial reciente")}</SectionLabel>
        <ul className="mt-2 divide-y divide-border">
          {history === null && (
            <li className="py-3"><div className="h-10 rounded bg-bg-muted animate-pulse" /></li>
          )}
          {history?.length === 0 && (
            <li className="py-6 text-center text-xs text-fg-muted">{t("Sin comandos enviados aún.")}</li>
          )}
          {history?.map((c) => {
            const m = comandoEstadoMeta[c.estado];
            return (
              <li key={c.id} className="py-2.5 flex items-start gap-3">
                <Terminal className="w-4 h-4 text-fg-subtle mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-fg">{t(comandoLabel[c.tipo])}</div>
                  <div className="text-[11px] text-fg-muted mt-0.5">
                    {formatDateTime(c.at)} · {c.por}
                    {c.detalle ? ` · ${c.detalle}` : ''}
                  </div>
                </div>
                <Badge tone={m.tone}>{t(m.label)}</Badge>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}

function AlarmasTab({ alarmas }) {
  const { t } = useTranslation();
  if (alarmas.length === 0) {
    return (
      <div className="text-center py-10">
        <div className="inline-flex w-10 h-10 rounded-full bg-status-success-soft text-status-success items-center justify-center mb-3">
          <Bell className="w-5 h-5" />
        </div>
        <div className="text-sm text-fg">{t("Sin alarmas registradas para esta unidad.")}</div>
      </div>
    );
  }
  return (
    <ul className="space-y-2">
      {alarmas.map((a) => (
        <li
          key={a.id}
          className={cn(
            'rounded-lg border p-3 flex items-start gap-3',
            a.resuelta ? 'border-border bg-bg' : 'border-border bg-bg-muted',
          )}
        >
          <div
            className={cn(
              'w-9 h-9 rounded-lg flex items-center justify-center shrink-0',
              a.severidad === 'critica' ? 'bg-status-error-soft text-status-error' :
              a.severidad === 'alta'    ? 'bg-status-warning-soft text-status-warning' :
                                          'bg-status-info-soft text-status-info',
            )}
          >
            <AlertTriangle className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-fg">{t(alarmaLabel[a.tipo])}</span>
              <Badge tone={severidadTone[a.severidad]} dot>{t(severidadLabel[a.severidad])}</Badge>
            </div>
            <p className="text-xs text-fg-muted mt-0.5">{a.mensaje}</p>
            <div className="mt-1 text-[11px] text-fg-subtle">
              {formatDateTime(a.at)}
              {a.resuelta && <span className="ml-2 text-status-success">· {t("Resuelta")}</span>}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function FichaTab({ device }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <section>
        <SectionLabel icon={<Cpu className="w-3.5 h-3.5" />}>{t("Equipo GPS")}</SectionLabel>
        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
          <Term>{t("IMEI")}</Term><Def mono>{device.imei}</Def>
          <Term>{t("Modelo")}</Term><Def>{device.modelo}</Def>
          <Term>{t("Protocolo")}</Term><Def>{device.protocolo}</Def>
          <Term>{t("Firmware")}</Term><Def mono>{device.firmware}</Def>
          <Term>{t("Instalado")}</Term><Def>{formatDateTime(device.instaladoEl)}</Def>
        </dl>
      </section>

      <section>
        <SectionLabel icon={<SignalHigh className="w-3.5 h-3.5" />}>{t("SIM")}</SectionLabel>
        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
          <Term>{t("Operador")}</Term><Def>{device.sim.operador}</Def>
          <Term>{t("ICCID")}</Term><Def mono>{device.sim.iccid}</Def>
          <Term>{t("Número")}</Term><Def mono>{device.sim.numero}</Def>
          <Term>{t("Plan / consumo")}</Term>
          <Def>
            <span className="tabular-nums">{device.sim.consumoMb}</span> / {device.sim.planMb} MB
            <span className="ml-2 inline-block align-middle w-16 h-1.5 rounded-full bg-bg-muted overflow-hidden">
              <span
                className={cn(
                  'block h-full',
                  device.sim.consumoMb / device.sim.planMb > 0.8 ? 'bg-status-error' : 'bg-status-info',
                )}
                style={{ width: `${Math.min(100, (device.sim.consumoMb / device.sim.planMb) * 100)}%` }}
              />
            </span>
          </Def>
        </dl>
      </section>

      <section>
        <SectionLabel icon={<Truck className="w-3.5 h-3.5" />}>{t("Vehículo vinculado")}</SectionLabel>
        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
          <Term>{t("Patente")}</Term><Def mono className="font-semibold">{device.patente}</Def>
          <Term>{t("Marca / modelo")}</Term><Def>{device.vehiculoMarca} {device.vehiculoModelo}</Def>
          <Term>{t("Tipo")}</Term><Def>{device.vehiculoTipo}</Def>
          <Term>{t("Vel. máxima")}</Term><Def><span className="tabular-nums">{device.velocidadMaxKmh}</span> km/h</Def>
        </dl>
      </section>

      <section>
        <SectionLabel icon={<User className="w-3.5 h-3.5" />}>{t("Conductor asignado")}</SectionLabel>
        <p className="mt-1.5 text-sm text-fg">
          {device.conductorNombre ?? <span className="text-fg-subtle italic">{t("Sin asignar")}</span>}
        </p>
      </section>

      <section>
        <SectionLabel icon={<Bell className="w-3.5 h-3.5" />}>{t("Alarmas configuradas")}</SectionLabel>
        <ul className="mt-2 space-y-1.5 text-sm">
          <ConfigLine on>{t("Exceso de velocidad")} &gt; {device.config.excesoVelocidadKmh} km/h</ConfigLine>
          <ConfigLine on={device.config.geocercas}>{t("Eventos de geocerca")}</ConfigLine>
          <ConfigLine on={device.config.bateriaDesconectada}>{t("Batería desconectada")}</ConfigLine>
          <ConfigLine on={device.config.panico}>{t("Botón de pánico")}</ConfigLine>
          <ConfigLine on={device.config.conduccionAgresiva}>{t("Conducción agresiva (frenado / aceleración)")}</ConfigLine>
          <ConfigLine on>{t("Parada prolongada")} &gt; {device.config.paradaProlongadaMin} min</ConfigLine>
        </ul>
      </section>
    </div>
  );
}

// ── Atoms ───────────────────────────────────────────────────────────────────

function SectionLabel({ children, icon }) {
  return (
    <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-fg-muted">
      {icon}
      {children}
    </div>
  );
}

function KpiTile({ label, value, unit, hint, icon, danger }) {
  return (
    <div className="rounded-lg bg-bg-muted px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-fg-muted">
        {icon}{label}
      </div>
      <div className="mt-0.5 flex items-baseline gap-1">
        <span className={cn('text-xl font-semibold tabular-nums', danger ? 'text-status-error' : 'text-fg')}>{value}</span>
        {unit && <span className="text-xs text-fg-muted">{unit}</span>}
      </div>
      {hint && <div className="mt-0.5 text-[10px] text-status-error">{hint}</div>}
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  return (
    <div className="rounded-md bg-bg-muted px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wide text-fg-muted">{label}</div>
      <div className={cn(
        'text-sm font-semibold tabular-nums mt-0.5',
        tone === 'warning' ? 'text-status-warning' : tone === 'error' ? 'text-status-error' : 'text-fg',
      )}>
        {value}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-fg-muted">{label}</div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function Term({ children }) {
  return <dt className="text-fg-muted text-xs uppercase tracking-wide font-medium pt-0.5">{children}</dt>;
}
function Def({ children, mono, className }) {
  return <dd className={cn('text-fg', mono && 'font-mono text-xs', className)}>{children}</dd>;
}
function ConfigLine({ children, on }) {
  return (
    <li className="flex items-center gap-2">
      <span
        className={cn('w-1.5 h-1.5 rounded-full shrink-0', on ? 'bg-status-success' : 'bg-fg-subtle/50')}
        aria-hidden
      />
      <span className={cn('text-fg', !on && 'text-fg-subtle line-through')}>{children}</span>
    </li>
  );
}
